<template>
    <div>
        <section class="section-head flex items-center justify-between">
            <page-title app="Admin" section="Logger" icon="logging-truck" />
        </section>
        <section class="section-main">
            <div>
                <div class="well justify-start mb-8">
                    <div class="flex flex-col mr-4">
                        <label
                            class="font-semibold uppercase leading-5 text-xs mb-1"
                            >Log</label
                        >
                        <select-dropdown v-model="log">
                            <option value="">All logs</option>
                            <option value="export">Export</option>
                            <option value="learn">Learn</option>
                            <option value="enrollmentFix">
                                Enrollment Fix
                            </option>
                            <option value="access-codes">Access Codes</option>
                            <option value="crm_webhook">CRM Webhook</option>
                            <option value="sync">Sync</option>
                            <option value="checkout">Checkout</option>
                        </select-dropdown>
                    </div>
                    <div class="flex flex-col mr-4">
                        <label
                            class="font-semibold uppercase leading-5 text-xs mb-1"
                            >Description contains</label
                        >
                        <input
                            type="text"
                            v-model="activity"
                            placeholder="Search description"
                            class="form-input block w-full text-gray-800 leading-5 bg-white border border-gray-200 rounded-lg p-3 text-sm"
                        />
                    </div>
                </div>
                <template v-if="!loading">
                    <div class="flex items-center justify-end mb-4">
                        <div class="flex items-center">
                            <div
                                class="flex items-center"
                                v-if="items.length > 0"
                            >
                                <span
                                    >Showing {{ resultStart }} –
                                    {{ resultEnd }} of {{ resultTotal }}</span
                                >
                                <button
                                    class="paginate-button ml-3 mr-1"
                                    :disabled="page == 1"
                                    @click="prevPage"
                                >
                                    &larr;
                                </button>
                                <button
                                    class="paginate-button"
                                    :disabled="page == pages"
                                    @click="nextPage"
                                >
                                    &rarr;
                                </button>
                            </div>
                            <select-dropdown
                                class="shadow ml-4"
                                size="sm"
                                v-model="pageSize"
                            >
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select-dropdown>
                        </div>
                    </div>
                    <div class="overflow-auto rounded shadow">
                        <table class="min-w-full divide-y divide-gray-200 bg-white">
                            <thead class="bg-gray-200">
                                <tr>
                                    <th
                                        scope="col"
                                        class="w-56 p-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
                                    >
                                        Date
                                    </th>
                                    <th
                                        scope="col"
                                        class="p-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
                                    >
                                        Description
                                    </th>
                                    <th
                                        scope="col"
                                        class="w-64 p-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
                                    >
                                        Properties
                                    </th>
                                    <th
                                        scope="col"
                                        class="w-64 p-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
                                    >
                                        Caused by
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100">
                                <tr
                                    v-for="item in items"
                                    :key="item.id"
                                    class="hover:bg-gray-50"
                                >
                                    <td
                                        class="p-4 text-sm"
                                        v-text="
                                            $filters.dateTime(
                                                item.created_at,
                                                user.timezone
                                            )
                                        "
                                    />
                                    <td class="p-4 font-mono"
                                        v-text="item.description"
                                    />
                                    <td class="p-4">
                                        <pre class="max-h-48 overflow-auto rounded p-3 text-xs border border-gray-700 whitespace-pre-wrap break-words">{{ item.properties }}</pre>
                                    </td>
                                    <td class="w-64 p-4">
                                        {{
                                            item.causer
                                                ? item.causer.name
                                                : "System"
                                        }}
                                    </td>
                                </tr>
                                <tr v-if="items.length === 0">
                                    <td
                                        colspan="4"
                                        class="p-8 text-center text-sm text-gray-500"
                                    >
                                        No logs found.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </template>
                <loading-spinner :show="loading" />
            </div>
        </section>
    </div>
</template>

<script lang="ts">
import axios from "axios";
import { mapState } from "vuex";
import { debounce } from "lodash";

export default {
    data(): any {
        return {
            loading: true,
            items: [],
            log: "",
            activity: "",
            pageSize: "50",
            page: 1,
            pages: null,
            resultStart: null,
            resultEnd: null,
            resultTotal: null,
        };
    },
    computed: {
        ...mapState(["user"]),
        queryParams() {
            return {
                log: this.log,
                activity: this.activity,
                pageSize: this.pageSize,
                page: this.page,
            };
        },
    },
    methods: {
        fetch() {
            this.loading = true;
            axios
                .get("/api/admin/logger", { params: this.queryParams })
                .then((response) => {
                    this.items = response.data.data;
                    this.resultStart = response.data.from;
                    this.resultEnd = response.data.to;
                    this.resultTotal = response.data.total;
                    this.pages = response.data.last_page;
                    this.loading = false;
                });
        },
        nextPage() {
            if (this.page != this.pages) {
                this.page++;
            }
        },
        prevPage() {
            if (this.page > 1) {
                this.page--;
            }
        },
        returnExamPath(subject) {
            if (subject && subject.exam && subject.exam.uuid && subject.order) {
                return (
                    '<a href="/prep/exams/' +
                    subject.exam.uuid +
                    "/" +
                    subject.order +
                    '" target="_blank">View in Exam</a>'
                );
            }
        },
    },
    created() {
        this.debouncedFetch = debounce(() => {
            this.page = 1;
            this.fetch();
        }, 400);
    },
    mounted() {
        this.fetch();
    },
    watch: {
        log() {
            this.page = 1;
            this.fetch();
        },
        activity() {
            this.debouncedFetch();
        },
        pageSize() {
            this.page = 1;
            this.fetch();
        },
        page() {
            this.fetch();
        },
    },
};
</script>
